import { useMemo } from "react";

import { Button } from "~/components/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/Dialog";
import { DiffLines } from "~/components/DiffLines";
import { SpinnerIcon } from "~/components/icon";

import {
  buildSettingsOverridesDiff,
  diffLinePrefix,
  diffSectionLabel,
  type SettingsOverrides,
} from "./helpers";

interface SettingsOverridesPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalOverrides: SettingsOverrides;
  draftOverrides: SettingsOverrides;
  canCreateSetConfig: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  onCreateOperation: () => void;
}

export function SettingsOverridesPreviewDialog({
  open,
  onOpenChange,
  originalOverrides,
  draftOverrides,
  canCreateSetConfig,
  isSubmitting,
  submitError,
  onCreateOperation,
}: SettingsOverridesPreviewDialogProps) {
  const diffSections = useMemo(
    () => buildSettingsOverridesDiff(originalOverrides, draftOverrides),
    [originalOverrides, draftOverrides],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden md:max-w-5xl">
        <Button
          type="button"
          size="sm"
          className="absolute right-12 top-4 h-6 px-2 text-xs"
          onClick={onCreateOperation}
          disabled={
            isSubmitting || !canCreateSetConfig || diffSections.length === 0
          }
        >
          {isSubmitting ? (
            <SpinnerIcon className="h-4 w-4 animate-spin" />
          ) : (
            "Create operation"
          )}
        </Button>
        <DialogHeader>
          <DialogTitle>Preview Settings Override Changes</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-gray-200 bg-slate-950 p-3">
          {diffSections.length === 0 ? (
            <div className="text-sm text-slate-300">No changes to preview.</div>
          ) : (
            <div className="space-y-4 font-mono text-xs leading-5">
              {diffSections.map((section) => (
                <div key={section.fileName}>
                  <div className="sticky top-0 mb-1 bg-slate-900 px-2 py-1 text-slate-200">
                    {diffSectionLabel(section)}
                  </div>
                  <DiffLines
                    lines={section.lines.map((line) => ({
                      style: line.type,
                      prefix: diffLinePrefix(line.type),
                      content: line.content,
                    }))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {submitError ? (
          <div className="text-sm text-red-600">{submitError}</div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
